import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  BaseView,
  BaseSafeFormView,
  HeaderNavigation,
  AppButton,
  SearchInput,
  WalletAddressItem,
  LoadingSpinner,
  DeleteAddressModal,
} from '@/components'
import { useWithdrawStore } from '@/store/useWithdrawStore'
import {
  useFetchWithdrawCryptoAddressByNetworkCode,
  CryptoAddressWithdrawDTO,
} from '@/domain/wallet'
import useOffsets from '@/hooks/useOffsets'

export const WithdrawChooseAddressScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const offsets = useOffsets()

  const [selectedAddress, setSelectedAddress] =
    useState<CryptoAddressWithdrawDTO>()
  const [searchValue, setSearchValue] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const blockchain = useWithdrawStore((state) => state.blockchain)
  const externalWalletAddress = useWithdrawStore(
    (state) => state.externalWalletAddress,
  )
  const setExternalWalletAddress = useWithdrawStore(
    (state) => state.setExternalWalletAddress,
  )
  const resetExternalWalletAddress = useWithdrawStore(
    (state) => state.resetExternalWalletAddress,
  )

  const { data, isLoading } = useFetchWithdrawCryptoAddressByNetworkCode({
    networkCode: blockchain?.code || '',
  })

  const addresses = data?.crypto_addresses || []

  const filteredAddresses = useMemo(() => {
    if (!searchValue.trim()) return addresses

    const normalizedSearch = searchValue.toLowerCase().trim()

    return addresses.filter(({ name, address }) => {
      const normalizedName = name.toLowerCase()
      const normalizedAddress = address.toLowerCase()

      return (
        normalizedName.includes(normalizedSearch) ||
        normalizedAddress.includes(normalizedSearch)
      )
    })
  }, [searchValue, addresses])

  const handleSelectAddress = useCallback(
    (address: CryptoAddressWithdrawDTO) => {
      const addressData = {
        id: address.id,
        name: address.name,
        address: address.address,
      }

      setExternalWalletAddress(addressData)

      navigate(-1) // Go back to review-and-confirm
    },
    [setExternalWalletAddress, navigate],
  )

  const handleDeleteAddress = useCallback(
    (address: CryptoAddressWithdrawDTO) => {
      setSelectedAddress(address)
      setShowDeleteConfirm(true)
    },
    [],
  )

  const handleRegisterNewAddress = () => {
    navigate('/withdraw/register-wallet-address')
  }

  // Auto-redirect to register if no addresses exist
  useEffect(() => {
    if (!addresses.length && !isLoading) {
      resetExternalWalletAddress()
      navigate(-1) // Go back and let the parent handle navigation
    }
  }, [addresses.length, isLoading, resetExternalWalletAddress, navigate])

  if (isLoading) {
    return (
      <BaseSafeFormView>
        <HeaderNavigation title={t('screens:withdraw.chooseAnAddress')} />
        <BaseView flex={1} center>
          <LoadingSpinner />
        </BaseView>
      </BaseSafeFormView>
    )
  }

  return (
    <BaseSafeFormView style={{ paddingTop: offsets.top }}>
      <HeaderNavigation title={t('screens:withdraw.chooseAnAddress')} />

      <BaseView px={13} flex={1} py={24} gap={20}>
        {/* Search */}
        <SearchInput
          placeholder={t('form:placeholders.search')}
          value={searchValue}
          onChangeText={setSearchValue}
          onClear={() => setSearchValue('')}
        />

        {/* Addresses List */}
        <BaseView flex={1} gap={12}>
          {filteredAddresses.length === 0 && !isLoading ? (
            <BaseView center py={40}>
              <p className="text-gray-400 text-center">
                {searchValue
                  ? t('common:noSearchResults')
                  : t('common:noAddressesFound')}
              </p>
            </BaseView>
          ) : (
            filteredAddresses.map((address) => (
              <WalletAddressItem
                key={address.id}
                {...address}
                onPress={handleSelectAddress}
                onPressDelete={() => handleDeleteAddress(address)}
              />
            ))
          )}
        </BaseView>
      </BaseView>

      {/* Add New Button */}
      <BaseView px={13} style={{ paddingBottom: offsets.bottom }}>
        <AppButton
          label={t('translation:addNew')}
          onPress={handleRegisterNewAddress}
          type="primaryGradient"
          style={{ width: '100%' }}
        />
      </BaseView>

      {/* Delete Confirmation Modal */}
      <DeleteAddressModal
        isVisible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        addressId={selectedAddress?.id || ''}
        addressName={selectedAddress?.name}
        onAfterDelete={() => {
          // If the deleted address is the same as the selected one, remove it
          if (
            selectedAddress?.address &&
            externalWalletAddress?.address &&
            selectedAddress.address === externalWalletAddress.address
          ) {
            resetExternalWalletAddress()
          }
        }}
      />
    </BaseSafeFormView>
  )
}
